import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

interface BulkUpdateResult {
  successful_updates: Array<{
    job_id: string;
    old_status: string;
    new_status: string;
    master_id: string;
  }>;
  failed_updates: Array<{
    job_id: string;
    master_id?: string;
    error: string;
    reason: string;
  }>;
  summary: {
    total_requested: number;
    successful: number;
    failed: number;
  };
}

interface BulkResultsDialogProps {
  open: boolean;
  onClose: () => void;
  results: BulkUpdateResult | null;
}

export default function BulkResultsDialog({
  open,
  onClose,
  results,
}: BulkResultsDialogProps) {
  if (!results) return null;

  const { successful_updates, failed_updates } = results;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" component="span">
            Bulk Status Update Results
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {successful_updates.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
            >
              <CheckCircleIcon color="success" fontSize="small" />
              Updated ({successful_updates.length})
            </Typography>
            <List dense>
              {successful_updates.map((update, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={update["master_id"] || update.job_id}
                    secondary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.5,
                        }}
                      >
                        <Chip
                          label={update["old_status"]}
                          size="small"
                          sx={{
                            backgroundColor: "#f8f9fa",
                            color: "#6c757d",
                            border: "1px solid #dee2e6",
                            fontWeight: "medium",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ mx: 1, color: "#6c757d" }}
                        >
                          →
                        </Typography>
                        <Chip
                          label={update["new_status"]}
                          size="small"
                          sx={{
                            backgroundColor: "#d1edff",
                            color: "#0066cc",
                            fontWeight: "medium",
                            border: "1px solid #bee5eb",
                          }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {failed_updates.length > 0 && (
          <Box>
            <Typography
              variant="h6"
              sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
            >
              <ErrorIcon color="error" fontSize="small" />
              Failed Updates ({failed_updates.length})
            </Typography>
            <List dense>
              {failed_updates.map((failure, index) => (
                <Box key={index}>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography variant="body2" fontWeight="medium">
                            {failure.master_id || failure.job_id}
                          </Typography>
                          <Chip
                            label={failure.error}
                            size="small"
                            sx={{
                              backgroundColor: "#ffebee",
                              color: "#c62828",
                              border: "1px solid #ffcdd2",
                              fontWeight: "medium",
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {failure.reason}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < failed_updates.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            mb: 1,
            mr: 1,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
